<script setup>
import { ref, watch, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from '../../socialMonitoring/components/FilterSelect.vue';
import { ASSET_TYPES, KANBAN_STATUSES, PLATFORMS } from '../utils/mediaMeta';

const props = defineProps({
  open: { type: Boolean, default: false },
  asset: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'save', 'delete']);

const form = ref({
  title: '',
  description: '',
  assetType: 'foto',
  kanbanStatus: 'briefing',
  tags: [],
  platformTarget: [],
  scheduledAt: '',
});
const newFiles = ref([]);
const newTag = ref('');

const isEdit = computed(() => !!props.asset?.id);
const typeOptions = ASSET_TYPES.map(t => ({
  value: t.value,
  label: t.label,
  icon: t.icon,
}));
const statusOptions = KANBAN_STATUSES.map(s => ({
  value: s.value,
  label: s.label,
}));

watch(
  () => [props.open, props.asset],
  () => {
    if (!props.open) return;
    newFiles.value = [];
    if (props.asset) {
      form.value = {
        title: props.asset.title,
        description: props.asset.description || '',
        assetType: props.asset.assetType,
        kanbanStatus: props.asset.kanbanStatus,
        tags: [...(props.asset.tags || [])],
        platformTarget: [...(props.asset.platformTarget || [])],
        scheduledAt: props.asset.scheduledAt
          ? props.asset.scheduledAt.slice(0, 16)
          : '',
      };
    } else {
      form.value = {
        title: '',
        description: '',
        assetType: 'foto',
        kanbanStatus: 'briefing',
        tags: [],
        platformTarget: [],
        scheduledAt: '',
      };
    }
  },
  { immediate: true }
);

function onFiles(e) {
  newFiles.value = Array.from(e.target.files);
}
function togglePlatform(p) {
  const idx = form.value.platformTarget.indexOf(p);
  if (idx === -1) form.value.platformTarget.push(p);
  else form.value.platformTarget.splice(idx, 1);
}
function addTag() {
  const t = newTag.value.trim();
  if (t && !form.value.tags.includes(t)) form.value.tags.push(t);
  newTag.value = '';
}
function removeTag(t) {
  form.value.tags = form.value.tags.filter(x => x !== t);
}

function submit() {
  const payload = { ...form.value };
  if (!payload.scheduledAt) delete payload.scheduledAt;
  if (props.asset?.id) {
    emit('save', { id: props.asset.id, payload, files: newFiles.value });
  } else {
    emit('save', { payload, files: newFiles.value });
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        @click="emit('close')"
      />
      <div
        class="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-n-background shadow-2xl"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-3"
        >
          <h3 class="text-base font-bold text-n-slate-12">
            {{ isEdit ? 'Editar asset' : 'Novo asset de mídia' }}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>

        <div class="flex-1 overflow-y-auto p-5">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Título</label>
              <input
                v-model="form.title"
                type="text"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Tipo</label>
              <FilterSelect
                v-model="form.assetType"
                :options="typeOptions"
                min-width="100%"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Status (kanban)</label>
              <FilterSelect
                v-model="form.kanbanStatus"
                :options="statusOptions"
                min-width="100%"
              />
            </div>

            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Plataformas-alvo</label>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="p in PLATFORMS"
                  :key="p.value"
                  type="button"
                  class="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition"
                  :class="[
                    form.platformTarget.includes(p.value)
                      ? `${p.color} border-transparent`
                      : 'border-n-strong/40 bg-n-background text-n-slate-11 hover:bg-n-slate-2',
                  ]"
                  @click="togglePlatform(p.value)"
                >
                  <span :class="p.icon" class="size-3" />
                  {{ p.label }}
                </button>
              </div>
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Agendado para</label>
              <input
                v-model="form.scheduledAt"
                type="datetime-local"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Tags</label>
              <div class="flex gap-2">
                <input
                  v-model="newTag"
                  type="text"
                  placeholder="adicionar tag"
                  class="flex-1 rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
                  @keydown.enter.prevent="addTag"
                />
                <Button
                  size="sm"
                  variant="outline"
                  icon="i-lucide-plus"
                  @click="addTag"
                />
              </div>
              <div v-if="form.tags.length" class="mt-2 flex flex-wrap gap-1">
                <span
                  v-for="t in form.tags"
                  :key="t"
                  class="inline-flex items-center gap-1 rounded bg-n-slate-2 px-1.5 py-0.5 text-[10px] text-n-slate-11"
                >
                  {{ t }}
                  <button
                    type="button"
                    class="hover:text-rose-600"
                    @click="removeTag(t)"
                  >
                    <span class="i-lucide-x size-2.5" />
                  </button>
                </span>
              </div>
            </div>

            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Descrição / briefing</label>
              <textarea
                v-model="form.description"
                rows="4"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>

            <div class="md:col-span-2">
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">
                {{
                  isEdit
                    ? 'Adicionar mais arquivos'
                    : 'Arquivos (imagens, vídeos, docs)'
                }}
              </label>
              <input
                type="file"
                multiple
                class="block w-full rounded-lg border border-dashed border-n-strong/40 bg-n-slate-2 p-3 text-xs"
                @change="onFiles"
              />
              <div
                v-if="isEdit && asset.files?.length"
                class="mt-3 grid grid-cols-3 gap-2"
              >
                <div
                  v-for="f in asset.files"
                  :key="f.id"
                  class="rounded-md border border-n-weak bg-n-slate-1 p-1"
                >
                  <img
                    v-if="f.contentType?.startsWith('image/')"
                    :src="f.url"
                    class="h-20 w-full rounded object-cover"
                    alt=""
                  />
                  <div v-else class="flex h-20 items-center justify-center">
                    <span class="i-lucide-file size-6 text-n-slate-11" />
                  </div>
                  <p class="mt-1 truncate text-[10px] text-n-slate-11">
                    {{ f.filename }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-n-weak px-5 py-3"
        >
          <Button
            v-if="isEdit"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            color="ruby"
            @click="emit('delete', asset)"
          >
            Excluir
          </Button>
          <span v-else />
          <div class="flex gap-2">
            <Button variant="outline" size="sm" @click="emit('close')">
              Cancelar
            </Button>
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :is-loading="saving"
              :disabled="!form.title.trim()"
              @click="submit"
            >
              {{ isEdit ? 'Salvar' : 'Criar' }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
